import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Clock, CheckCircle2, FileText, ClipboardCheck, ChevronRight, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

/**
 * V14 — BCM Manager "Pending Approvals" read-only widget.
 * Surfaces the decentralized approval queue (BIAs + Plans) in the Mission
 * Control dashboard so BCM Managers can chase stalled items without
 * stepping into the approval chain itself.
 */
export default function PendingApprovalsWidget() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(false);

  const loadPending = async () => {
    try {
      const r = await api.get("/bcm-admin/dashboard/pending-approvals");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load pending approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDispatchReminders = async () => {
    setDispatching(true);
    try {
      const r = await api.post("/ai-v14/followup/dispatch", {
        stale_days: 3,
        max_items: 20,
        dry_run: false,
      });
      if (r.data?.dispatched > 0) {
        toast.success(`Sent ${r.data.dispatched} reminder${r.data.dispatched === 1 ? "" : "s"}`);
      } else {
        toast.info(r.data?.message || "No stalled items to remind on");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to dispatch reminders");
    } finally {
      setDispatching(false);
    }
  };

  if (loading) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
        data-testid="pending-approvals-widget-loading"
      >
        <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">
          Pending Approvals
        </p>
        <div className="h-20 animate-pulse bg-slate-50 rounded-lg mt-3" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">
          Pending Approvals
        </p>
        <p className="text-sm text-slate-500 mt-3">
          {error || "Unavailable"}
        </p>
      </div>
    );
  }

  const c = data.counts || {};
  const total = c.total_open || 0;
  const stalled = c.stalled_3_plus_days || 0;

  const rows = [
    {
      icon: ClipboardCheck,
      label: "BIA — 1st approval (PM / IT Fn Mgr)",
      count: c.bia_awaiting_first_approval || 0,
      tone: "bg-sky-100 text-sky-700",
      link: "/operational-bia",
    },
    {
      icon: ClipboardCheck,
      label: "BIA — Dept Head final",
      count: c.bia_awaiting_department_head || 0,
      tone: "bg-emerald-100 text-emerald-700",
      link: "/operational-bia",
    },
    {
      icon: FileText,
      label: "Plans — 1st approval",
      count: c.plan_awaiting_first_approval || 0,
      tone: "bg-amber-100 text-amber-700",
      link: "/recovery-plans",
    },
    {
      icon: FileText,
      label: "Plans — Dept Head final",
      count: c.plan_awaiting_department_head || 0,
      tone: "bg-purple-100 text-purple-700",
      link: "/recovery-plans",
    },
  ];

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
      data-testid="pending-approvals-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">
            Pending Approvals
          </p>
          <div className="flex items-baseline gap-3 mt-1">
            <p
              className="text-2xl font-extrabold text-slate-800"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="pending-approvals-total"
            >
              {total}
            </p>
            {stalled > 0 && (
              <span
                className="inline-flex items-center gap-1.5 text-[0.7rem] font-bold text-rose-600 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full"
                data-testid="pending-approvals-stalled"
              >
                <AlertCircle className="w-3 h-3" />
                {stalled} stalled &gt; 3d
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDispatchReminders}
            disabled={dispatching || total === 0}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 text-[0.7rem] font-semibold hover:bg-purple-100 hover:border-purple-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="dispatch-reminders-btn"
            title="Generate AI reminders and drop them into recipients' notification bells"
          >
            {dispatching ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Bell className="w-3.5 h-3.5" />
            )}
            {dispatching ? "Sending…" : "Send Reminders"}
          </button>
          <div className="w-9 h-9 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
            <Clock className="w-4 h-4" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <Link
            key={r.label}
            to={r.link}
            className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
            data-testid={`pending-row-${r.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-7 h-7 rounded-md ${r.tone} flex items-center justify-center flex-shrink-0`}>
                <r.icon className="w-3.5 h-3.5" />
              </div>
              <span className="text-xs text-slate-600 group-hover:text-slate-800 truncate">
                {r.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span
                className={`text-sm font-bold ${
                  r.count > 0 ? "text-slate-800" : "text-slate-300"
                }`}
              >
                {r.count}
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500" />
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 text-[0.7rem] text-slate-500 leading-relaxed flex items-start gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
        <span>
          Read-only view — approvals route to Process / IT Function Managers (1st) and Department Heads (final).
        </span>
      </div>
    </div>
  );
}
